// frame: sTK49 @ ui/dogdisk-user.pen — 数字分页 + 跳转。
import { useState } from 'react'

type Props = {
  page: number
  total: number
  pageSize: number
  onChange: (page: number) => void
}

// 计算要渲染的页码序列(夹 1 / 末页固定,中间滑动窗口,过远处折叠为 '…')。
// 边界处自动外扩窗口,保证首/末页附近连续展示若干页(例如 page=1 → 1 2 3 … N)。
function pageItems(page: number, totalPages: number): Array<number | '…'> {
  const out: Array<number | '…'> = []
  let start = page - 1
  let end = page + 1
  if (page <= 3) {
    start = 2
    end = Math.min(4, totalPages - 1)
  }
  if (page >= totalPages - 2) {
    start = Math.max(2, totalPages - 3)
    end = totalPages - 1
  }
  const window: number[] = []
  for (let n = start; n <= end; n++) if (n > 1 && n < totalPages) window.push(n)
  out.push(1)
  if (window[0] && window[0] > 2) out.push('…')
  window.forEach((n) => out.push(n))
  if (totalPages > 1) {
    const last = window[window.length - 1]
    if (last && last < totalPages - 1) out.push('…')
    out.push(totalPages)
  }
  return out
}

export function Pagination({ page, total, pageSize, onChange }: Props) {
  const totalPages = Math.ceil(total / pageSize)
  const [jump, setJump] = useState('')
  if (totalPages <= 1) return null

  const go = (n: number) => {
    const clamped = Math.min(Math.max(1, n), totalPages)
    if (clamped !== page) onChange(clamped)
  }

  return (
    <div data-testid="sTK49" className="flex flex-col items-center gap-3 py-4">
      <div className="flex items-center gap-1.5">
        <PageBtn disabled={page <= 1} onClick={() => go(page - 1)} aria-label="上一页">‹</PageBtn>
        {pageItems(page, totalPages).map((it, i) =>
          it === '…' ? (
            <span key={`e${i}`} className="px-1 text-ink-mute">…</span>
          ) : (
            <button
              key={it}
              type="button"
              onClick={() => go(it)}
              className={
                'h-8 min-w-8 rounded-lg px-2 text-[13px] ' +
                (it === page ? 'bg-brand-500 text-white' : 'bg-surface-100 text-ink active:bg-surface-200')
              }
            >
              {it}
            </button>
          ),
        )}
        <PageBtn disabled={page >= totalPages} onClick={() => go(page + 1)} aria-label="下一页">›</PageBtn>
      </div>
      <div className="flex items-center gap-2 text-[12px] text-ink-mute">
        <span>共 {totalPages} 页,跳至</span>
        <input
          data-testid="sTK49-jump-input"
          inputMode="numeric"
          value={jump}
          onChange={(e) => setJump(e.target.value.replace(/[^0-9]/g, ''))}
          className="h-8 w-12 rounded-lg border border-border-subtle text-center text-ink"
        />
        <span>页</span>
        <button
          type="button"
          data-testid="sTK49-jump-btn"
          onClick={() => { if (jump) { go(Number(jump)); setJump('') } }}
          className="h-8 rounded-lg bg-brand-500 px-3 text-white"
        >
          跳转
        </button>
      </div>
    </div>
  )
}

function PageBtn({ disabled, onClick, children, ...rest }: {
  disabled?: boolean; onClick: () => void; children: React.ReactNode } & React.AriaAttributes) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-100 text-ink disabled:opacity-40"
      {...rest}
    >
      {children}
    </button>
  )
}
